// =====================================================
// 产品矩阵管理系统 - 项目服务
// 处理画布项目的保存、加载、删除等操作
// =====================================================

import { supabase } from '@/config/supabase';
import type { CanvasProject, CanvasData, LayoutType, StyleType } from '../types';

// =====================================================
// 类型定义
// =====================================================

/** 数据库中的项目结构 */
interface DbCanvasProject {
  id: string;
  name: string;
  description: string | null;
  template_id: string | null;
  layout_type: string;
  style_type: string;
  canvas_data: any;
  canvas_settings: any;
  is_draft: boolean;
  is_public: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/** 创建项目参数 */
export interface CreateProjectParams {
  name: string;
  description?: string;
  layoutType: LayoutType;
  styleType: StyleType;
  canvasData: CanvasData;
  canvasSettings?: any;
  isDraft?: boolean;
  isPublic?: boolean;
}

/** 更新项目参数 */
export interface UpdateProjectParams {
  name?: string;
  description?: string;
  layoutType?: LayoutType;
  styleType?: StyleType;
  canvasData?: CanvasData;
  canvasSettings?: any;
  isDraft?: boolean;
  isPublic?: boolean;
}

// =====================================================
// 转换函数
// =====================================================

/** 数据库记录转前端类型 */
function dbToProject(db: DbCanvasProject): CanvasProject {
  return {
    id: db.id,
    name: db.name,
    description: db.description || undefined,
    template_id: db.template_id || undefined,
    layout_type: db.layout_type as LayoutType,
    style_type: db.style_type as StyleType,
    canvas_data: db.canvas_data as CanvasData,
    canvas_settings: db.canvas_settings,
    is_draft: db.is_draft,
    is_public: db.is_public,
    created_by: db.created_by || undefined,
    updated_by: db.updated_by || undefined,
    created_at: db.created_at,
    updated_at: db.updated_at,
  };
}

// =====================================================
// API 函数
// =====================================================

/**
 * 获取用户的所有项目
 */
export async function fetchProjects(): Promise<CanvasProject[]> {
  const { data, error } = await supabase
    .from('ditu_canvas_projects')
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }

  return (data || []).map(dbToProject);
}

/**
 * 获取单个项目
 */
export async function fetchProjectById(id: string): Promise<CanvasProject | null> {
  const { data, error } = await supabase
    .from('ditu_canvas_projects')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // 未找到
    }
    console.error('Error fetching project:', error);
    throw error;
  }

  return data ? dbToProject(data) : null;
}

/**
 * 创建新项目
 */
export async function createProject(params: CreateProjectParams): Promise<CanvasProject> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  const { data, error } = await supabase
    .from('ditu_canvas_projects')
    .insert({
      name: params.name,
      description: params.description || null,
      layout_type: params.layoutType,
      style_type: params.styleType,
      canvas_data: params.canvasData,
      canvas_settings: params.canvasSettings || {},
      is_draft: params.isDraft ?? true,
      is_public: params.isPublic ?? false,
      created_by: userId,
      updated_by: userId,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating project:', error);
    throw error;
  }

  return dbToProject(data);
}

/**
 * 更新项目
 */
export async function updateProject(
  id: string,
  params: UpdateProjectParams
): Promise<CanvasProject> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  const updateData: any = {
    updated_by: userId,
    updated_at: new Date().toISOString(),
  };

  if (params.name !== undefined) updateData.name = params.name;
  if (params.description !== undefined) updateData.description = params.description;
  if (params.layoutType !== undefined) updateData.layout_type = params.layoutType;
  if (params.styleType !== undefined) updateData.style_type = params.styleType;
  if (params.canvasData !== undefined) updateData.canvas_data = params.canvasData;
  if (params.canvasSettings !== undefined) updateData.canvas_settings = params.canvasSettings;
  if (params.isDraft !== undefined) updateData.is_draft = params.isDraft;
  if (params.isPublic !== undefined) updateData.is_public = params.isPublic;

  const { data, error } = await supabase
    .from('ditu_canvas_projects')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating project:', error);
    throw error;
  }

  return dbToProject(data);
}

/**
 * 保存项目（创建或更新）
 */
export async function saveProject(
  project: CanvasProject
): Promise<CanvasProject> {
  if (project.id) {
    return updateProject(project.id, {
      name: project.name,
      description: project.description,
      layoutType: project.layout_type,
      styleType: project.style_type,
      canvasData: project.canvas_data,
      canvasSettings: project.canvas_settings,
      isDraft: project.is_draft,
      isPublic: project.is_public,
    });
  } else {
    return createProject({
      name: project.name,
      description: project.description,
      layoutType: project.layout_type,
      styleType: project.style_type,
      canvasData: project.canvas_data,
      canvasSettings: project.canvas_settings,
      isDraft: project.is_draft,
      isPublic: project.is_public,
    });
  }
}

/**
 * 删除项目
 */
export async function deleteProject(id: string): Promise<void> {
  const { error } = await supabase
    .from('ditu_canvas_projects')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
}

/**
 * 复制项目
 */
export async function duplicateProject(id: string): Promise<CanvasProject> {
  const original = await fetchProjectById(id);
  if (!original) {
    throw new Error('Project not found');
  }

  return createProject({
    name: `${original.name} (副本)`,
    description: original.description,
    layoutType: original.layout_type,
    styleType: original.style_type,
    canvasData: original.canvas_data,
    canvasSettings: original.canvas_settings,
    isDraft: true,
    isPublic: false,
  });
}

/**
 * 自动保存项目（防抖）
 */
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

export function autoSaveProject(
  project: CanvasProject,
  delay = 3000
): Promise<CanvasProject> {
  return new Promise((resolve, reject) => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    autoSaveTimer = setTimeout(async () => {
      try {
        const saved = await saveProject(project);
        resolve(saved);
      } catch (error) {
        reject(error);
      }
    }, delay);
  });
}

/**
 * 取消自动保存
 */
export function cancelAutoSave(): void {
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
    autoSaveTimer = null;
  }
}
